import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/Button/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card/Card';
import { ResearchService, type SubmittedProposalsResponse } from '@/services/ResearchService';

type Group = SubmittedProposalsResponse['grouped_by_city'][number];
const keyOf = (group: Group) => JSON.stringify([group.city, group.country]);
const service = new ResearchService();

function OrderRow({
  group,
  index,
  count,
  disabled,
  move,
}: {
  group: Group;
  index: number;
  count: number;
  disabled: boolean;
  move: (from: number, to: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: keyOf(group),
    disabled,
  });
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
      className="flex items-center gap-3 rounded-lg border bg-background p-3"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        disabled={disabled}
        aria-label={`Drag ${group.city}`}
        className="touch-none rounded p-1 text-muted-foreground focus-visible:ring-2 focus-visible:ring-teal-600"
      >
        <GripVertical size={18} />
      </button>
      <span className="text-sm text-muted-foreground w-5">{index + 1}</span>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{group.city}</div>
        <div className="text-xs text-muted-foreground">{group.country}</div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled || index === 0}
        aria-label={`Move ${group.city} up`}
        onClick={() => move(index, index - 1)}
      >
        <ArrowUp size={16} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled || index === count - 1}
        aria-label={`Move ${group.city} down`}
        onClick={() => move(index, index + 1)}
      >
        <ArrowDown size={16} />
      </Button>
    </li>
  );
}

export function DestinationOrderPanel({
  eventId,
  groups,
  onSaved,
}: {
  eventId: string;
  groups: Group[];
  onSaved: (groups: Group[]) => void;
}) {
  const [draft, setDraft] = useState(groups);
  const [expected, setExpected] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  useEffect(() => {
    setDraft(groups);
  }, [groups]);
  useEffect(() => {
    let active = true;
    service
      .getProposalDestinationOrder(eventId)
      .then((result) => {
        if (active) setExpected(result.destination_ids);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [eventId]);
  const dirty = draft.map(keyOf).join('|') !== groups.map(keyOf).join('|');
  const disabled =
    saving || expected === null || groups.some((g) => !g.proposal_destination_ids?.length);
  const move = (from: number, to: number) => {
    setDraft((current) => arrayMove(current, from, to));
    setMessage('');
  };
  const drop = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id)
      move(
        draft.findIndex((g) => keyOf(g) === active.id),
        draft.findIndex((g) => keyOf(g) === over.id),
      );
  };
  async function save() {
    if (disabled || !dirty) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await service.saveProposalDestinationOrder(
        eventId,
        [...new Set(draft.flatMap((g) => g.proposal_destination_ids))],
        expected!,
      );
      setExpected(result.destination_ids);
      onSaved(draft);
      setMessage('Destination order saved. Refresh any open client proposal to see it.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save destination order.');
    } finally {
      setSaving(false);
    }
  }
  if (groups.length < 2) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Destination order</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Drag destinations or use the arrows. This order appears on the client proposal cover, home
          page and navigation. Hotels and activities stay with their destinations.
        </p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={drop}>
          <SortableContext items={draft.map(keyOf)} strategy={verticalListSortingStrategy}>
            <ol aria-label="Proposal destination order" className="space-y-2">
              {draft.map((group, index) => (
                <OrderRow
                  key={keyOf(group)}
                  group={group}
                  index={index}
                  count={draft.length}
                  disabled={disabled}
                  move={move}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
        {error && (
          <p role="alert" className="text-sm text-red-600 mt-3">
            {error}
          </p>
        )}
        {message && (
          <p role="status" className="text-sm text-teal-700 mt-3">
            {message}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button
            type="button"
            variant="ghost"
            disabled={saving || !dirty}
            onClick={() => {
              setDraft(groups);
              setError('');
              setMessage('');
            }}
          >
            Cancel
          </Button>
          <Button type="button" disabled={disabled || !dirty} onClick={save}>
            {saving ? 'Saving…' : 'Save destination order'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
